'use es6';

import { Record, Set as ImmutableSet, List, Map as ImmutableMap } from 'immutable';
import { MINIMUM_TARGETABLE_AGE, MAXIMUM_TARGETABLE_AGE, FBGeolocationTypes } from 'AdsUI/lib/Constants';
import { getLocationKeysByGeolocationType } from 'AdsUI/lib/LeadAdCreationUtils';
import FilterGroupRecord, { FilterGroupTypes } from './FilterGroupRecord';
import GeolocationRecord from 'AdsUI/records/audiences/GeolocationRecord';
import { DetailedTargetingEntityRecord } from './DetailedTargetingRecords';
import FBSavedAudienceRecord from '../audiences/FBSavedAudienceRecord';
import AudienceNetworkStateRecord from '../audiences/AudienceNetworkStateRecord';
import FBTargetingRecord from './FBTargetingRecord';
export const SequenceAudienceTypes = {
  ALL_CUSTOMERS: 'ALL_CUSTOMERS',
  ENGAGED_LAST_30_DAYS: 'ENGAGED_LAST_30_DAYS',
  PEOPLE_WHO_VISIT_STEP_1: 'PEOPLE_WHO_VISIT_STEP_1',
  PEOPLE_WHO_WATCH_STEP_1: 'PEOPLE_WHO_WATCH_STEP_1',
  PEOPLE_WHO_SUBMIT_STEP_2: 'PEOPLE_WHO_SUBMIT_STEP_2',
  PEOPLE_WHO_SUBMIT_STEP_3: 'PEOPLE_WHO_SUBMIT_STEP_3',
  PEOPLE_WHO_CLICK_STEP_2: 'PEOPLE_WHO_CLICK_STEP_2',
  LOOKALIKE_AUDIENCE: 'LOOKALIKE_AUDIENCE'
};
export default class AdCreationSequenceTargetingRecord extends Record({
  locations: ImmutableSet(),
  audiences: ImmutableSet(),
  exclusions: ImmutableSet(),
  filterGroups: List(),
  selectedLookalike: null,
  selectedPixel: null,
  locale: null,
  estimatedSize: null,
  specialCategory: null,
  savedAudience: null,
  dsaInfo: null
}) {
  static getTargetingFromPublishedAd(targeting, exclusions, FBTargeting) {
    const parsedTargeting = JSON.parse(targeting);
    const {
      locations
    } = FBTargetingRecord.fromTargeting(parsedTargeting);
    const {
      flexible_spec,
      custom_audiences
    } = parsedTargeting;

    const _filterGroups = flexible_spec && FBTargeting.length > 0 && flexible_spec.map(group => {
      const segments = [];
      Object.keys(group).forEach(segment_type => {
        group[segment_type].forEach(segment => {
          const segmentId = typeof segment === 'number' ? segment.toString() : segment.id;
          const capitalizedType = segment_type.toUpperCase();
          const matchedSegment = FBTargeting.find(FBSegment => segmentId === FBSegment.id && capitalizedType === FBSegment.type);

          if (matchedSegment) {
            const {
              id,
              name,
              type: facet,
              audienceSize,
              path
            } = matchedSegment;
            segments.push({
              id,
              name,
              facet,
              audienceSize,
              group: path[path.length - 1]
            });
          }
        });
        return segments;
      });
      return {
        type: 'detailedTargeting',
        audiences: [],
        detailedTargeting: segments
      };
    }) || [];

    return new AdCreationSequenceTargetingRecord({
      locations: ImmutableSet(locations.map(loc => new GeolocationRecord({
        key: loc,
        name: loc
      }))),
      audiences: ImmutableSet(custom_audiences || []),
      exclusions,
      filterGroups: List(_filterGroups.map(group => FilterGroupRecord({
        type: group.type,
        audiences: ImmutableSet(group.audiences.map(audience => new AudienceNetworkStateRecord(audience))),
        detailedTargeting: ImmutableSet(group.detailedTargeting.map(targetingEntity => new DetailedTargetingEntityRecord(targetingEntity)))
      })))
    });
  }

  static getTargetingFromDraft(network, draft) {
    return new AdCreationSequenceTargetingRecord(Object.assign({}, draft, {
      dsaInfo: draft.dsaInfo && new ImmutableMap(draft.dsaInfo),
      locations: ImmutableSet(draft.locations.map(loc => new GeolocationRecord(loc))),
      audiences: ImmutableSet(draft.audiences),
      exclusions: ImmutableSet(draft.exclusions),
      savedAudience: draft.savedAudience && FBSavedAudienceRecord(draft.savedAudience),
      filterGroups: List(draft.filterGroups.map(group => FilterGroupRecord({
        type: group.type,
        audiences: ImmutableSet(group.audiences.map(audience => new AudienceNetworkStateRecord(audience))),
        detailedTargeting: ImmutableSet(group.detailedTargeting.map(targetingEntity => new DetailedTargetingEntityRecord(targetingEntity)))
      })))
    }));
  }

  hasAudiences() {
    return this.locations.size > 0 || this.exclusions.size > 0;
  }

  getRequestData() {
    if (this.savedAudience) {
      return {
        savedAudienceId: this.savedAudience.id
      };
    }

    const detailedTargeting = this.filterGroups.filter(group => group.type === FilterGroupTypes.DETAILED_TARGETING).map(group => group.detailedTargeting.map(targeting => ({
      id: targeting.id,
      name: targeting.name,
      type: targeting.facet || targeting.type,
      audienceSize: targeting.audienceSize
    }))).toJS();
    return Object.assign({}, this.dsaInfo && {
      dsaInfo: this.dsaInfo.toJS()
    }, {
      targetingOptions: {
        countryGroups: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.COUNTRY_GROUP),
        countries: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.COUNTRY),
        regions: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.REGION),
        cities: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.CITY),
        geoMarkets: getLocationKeysByGeolocationType(this.locations, FBGeolocationTypes.GEO_MARKET),
        customAudienceIds: this.selectedLookalike ? [this.selectedLookalike.id] : [],
        excludedCustomAudienceIds: this.excludedAudiences ? this.excludedAudiences.map(audience => audience.id).toArray() : [],
        detailedTargeting,
        ageMin: MINIMUM_TARGETABLE_AGE,
        ageMax: MAXIMUM_TARGETABLE_AGE
      },
      specialAdCategory: this.specialCategory,
      // NOTE: PEOPLE_WHO_VISIT_STEP_1 is not currently supported due to issues with the pixel.
      // As a result, there are no possible options for targeting audiences on the convert step.
      //
      //shouldTargetWebsiteVisitors: this.audiences.has(
      //  SequenceAudienceTypes.PEOPLE_WHO_VISIT_STEP_1
      //),
      //pixelId:
      //  this.audiences.has(SequenceAudienceTypes.PEOPLE_WHO_VISIT_STEP_1) &&
      //  this.selectedPixel &&
      //  this.selectedPixel.id,
      //shouldTargetPostEngagementAudience: this.audiences.has(
      //  SequenceAudienceTypes.ENGAGED_LAST_30_DAYS
      //),
      //shouldTargetWhoWatchedVideo: this.audiences.has(
      //  SequenceAudienceTypes.PEOPLE_WHO_WATCH_STEP_1
      //),
      shouldTargetWebsiteVisitors: false
    });
  }

}